import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api, type Holding } from "../api.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { Money } from "../components/Money.js";
import { csvMoney, downloadCsv } from "../lib/csv.js";
import { cn } from "../lib/utils.js";
import { DataTable, uniq, type Column } from "../components/data-table.js";

export function StocksPositions(): JSX.Element {
  const { formatNumber } = useNumberFormat();
  const holdings = useQuery({ queryKey: ["holdings"], queryFn: () => api.holdings() });
  const rows = holdings.data?.items ?? [];
  const lastUpdated = rows[0]?.observed_at ?? null;

  const exportCsv = (): void => {
    downloadCsv(
      "positions.csv",
      ["Symbol", "Name", "ISIN", "Account", "Broker", "Class", "Currency", "Quantity", "Price", "MarketValue", "ValueEUR", "UnrealizedPL_EUR", "Weight%"],
      rows.map((h) => [
        h.symbol, h.name ?? "", h.isin ?? "", h.account_name, h.provider ?? "", h.asset_class,
        h.currency, h.quantity, csvMoney(h.mark_price_minor), csvMoney(h.mark_value_minor),
        csvMoney(h.value_eur_minor), csvMoney(h.unrealized_pl_eur_minor), h.weight_pct.toFixed(2),
      ]),
    );
  };

  const columns: Column<Holding>[] = [
    {
      key: "symbol",
      label: "Symbol",
      sort: (a, b) => a.symbol.localeCompare(b.symbol),
      filter: {
        kind: "search",
        accessor: (h) => `${h.symbol} ${h.name ?? ""} ${h.isin ?? ""}`,
        placeholder: "Symbol / name / ISIN…",
      },
      cell: (h) =>
        h.security_id ? (
          <Link to={`/stocks/s/${h.security_id}`} className="text-brand hover:underline">
            <div className="font-medium">{h.symbol}</div>
            {h.name && <div className="text-muted-foreground text-xs">{h.name}</div>}
          </Link>
        ) : (
          <>
            <div className="font-medium">{h.symbol}</div>
            {h.name && <div className="text-muted-foreground text-xs">{h.name}</div>}
          </>
        ),
    },
    {
      key: "account",
      label: "Account",
      sort: (a, b) => a.account_name.localeCompare(b.account_name),
      filter: { kind: "select", accessor: (h) => h.account_name, options: (rs) => uniq(rs.map((h) => h.account_name)) },
      cell: (h) => (
        <>
          <div>{h.account_name}</div>
          {h.provider && <div className="text-muted-foreground text-xs">{h.provider}</div>}
        </>
      ),
    },
    {
      key: "class",
      label: "Class",
      sort: (a, b) => a.asset_class.localeCompare(b.asset_class),
      filter: { kind: "select", accessor: (h) => h.asset_class, options: (rs) => uniq(rs.map((h) => h.asset_class)) },
      cell: (h) => h.asset_class,
    },
    {
      key: "qty",
      label: "Qty",
      align: "right",
      sort: (a, b) => Number(a.quantity) - Number(b.quantity),
      cell: (h) => formatNumber(Number(h.quantity)),
    },
    {
      key: "price",
      label: "Price",
      align: "right",
      cell: (h) => (h.mark_price_minor != null ? <Money amountMinor={h.mark_price_minor} native={h.currency} /> : "—"),
    },
    {
      key: "value",
      label: "Value",
      align: "right",
      sort: (a, b) => a.value_eur_minor - b.value_eur_minor,
      cell: (h) => (
        <span className="font-medium">
          {h.mark_value_minor != null ? <Money amountMinor={h.mark_value_minor} native={h.currency} /> : "—"}
        </span>
      ),
    },
    {
      key: "pl",
      label: "Unrl. P&L",
      align: "right",
      sort: (a, b) => (a.unrealized_pl_eur_minor ?? 0) - (b.unrealized_pl_eur_minor ?? 0),
      cell: (h) => (
        <span className={cn((h.unrealized_pl_eur_minor ?? 0) < 0 ? "text-bad" : "text-good")}>
          {h.unrealized_pl_eur_minor != null ? <Money amountMinor={h.unrealized_pl_eur_minor} native="EUR" /> : "—"}
        </span>
      ),
    },
    {
      key: "weight",
      label: "Weight",
      align: "right",
      sort: (a, b) => a.weight_pct - b.weight_pct,
      cell: (h) => `${h.weight_pct.toFixed(1)}%`,
    },
  ];

  return (
    <>
      <PageHeader title="Positions">
        <PageActions
          updatedAt={lastUpdated}
          actions={[{ label: "Export CSV", icon: Download, onClick: exportCsv }]}
        />
      </PageHeader>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(h) => `${h.account_id}-${h.security_id ?? h.symbol}`}
        defaultSort="value"
        loading={holdings.isLoading}
        emptyText={
          rows.length === 0
            ? "No positions yet — they appear after the next broker sync."
            : "No positions match these filters."
        }
      />
    </>
  );
}
